import { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import api from '../lib/api';

// First sign-in for accounts created for someone (superadmin-added content
// publishers / event leaders, and invitees provisioned with a temporary
// password — design/018 P4, design/027). Shown full-screen (gated by
// me.mustChangePassword) until the person has chosen a username, filled in
// any missing name, and replaced the temporary password.
const USERNAME_RE = /^[a-zA-Z0-9._-]{3,30}$/;

function passwordProblem(pw) {
  if (pw.length < 8) return 'New password must be at least 8 characters.';
  if (!/[a-zA-Z]/.test(pw) || !/[0-9]/.test(pw)) return 'New password must include a letter and a number.';
  return null;
}

export default function ChangePasswordPage() {
  const { setNewPassword, refreshMe, signOut, me } = useAuth();
  // Ask for names only when they're missing; username always (it starts as
  // the email placeholder for these accounts).
  const [needFirst] = useState(!me?.firstname);
  const [needLast] = useState(!me?.lastname);
  const [firstname, setFirstname] = useState(me?.firstname || '');
  const [lastname, setLastname] = useState(me?.lastname || '');
  const [username, setUsername] = useState(me?.username && me.username !== me.email ? me.username : '');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (needFirst && !firstname.trim()) { setError('Please enter your first name.'); return; }
    if (needLast && !lastname.trim()) { setError('Please enter your last name.'); return; }
    if (!USERNAME_RE.test(username.trim())) {
      setError('Username must be 3–30 characters (letters, numbers, . _ -).'); return;
    }
    const pwErr = passwordProblem(next);
    if (pwErr) { setError(pwErr); return; }
    if (next !== confirm) { setError('Passwords do not match.'); return; }
    setBusy(true);
    try {
      // Profile first: a taken username fails here, before the password changes.
      const profile = { username: username.trim() };
      if (needFirst) profile.firstname = firstname.trim();
      if (needLast) profile.lastname = lastname.trim();
      try {
        await api.updateMe(profile);
      } catch (pErr) {
        const msg = pErr?.data?.error || pErr?.data?.message || pErr?.message || '';
        setError(/username/i.test(msg) ? 'That username is taken — please choose another.' : (msg || 'Could not save your details.'));
        setBusy(false);
        return;
      }
      await setNewPassword(next);
      await api.markPasswordChanged();
      await refreshMe();   // mustChangePassword flips false → app proceeds
    } catch (err) {
      if (err?.code === 'auth/requires-recent-login') {
        // Signed in too long ago — Firebase wants a fresh sign-in first.
        alert('For security, please sign in again with your temporary password to finish setting up your account.');
        await signOut();
        return;
      }
      setError(friendly(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={styles.wrap}>
      <form style={styles.card} onSubmit={submit}>
        <h1 style={styles.title}>Complete your account</h1>
        <p style={styles.sub}>
          You signed in with a temporary password{me?.email ? ` for ${me.email}` : ''}. Choose a username
          and a new password to continue.
        </p>

        {(needFirst || needLast) && (
          <div style={{ display: 'flex', gap: 10 }}>
            {needFirst && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={styles.label}>First name *</label>
                <input style={styles.input} type="text" value={firstname}
                  onChange={(e) => setFirstname(e.target.value)} autoComplete="given-name" required />
              </div>
            )}
            {needLast && (
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={styles.label}>Last name *</label>
                <input style={styles.input} type="text" value={lastname}
                  onChange={(e) => setLastname(e.target.value)} autoComplete="family-name" required />
              </div>
            )}
          </div>
        )}

        <label style={styles.label}>Username *</label>
        <input style={styles.input} type="text" value={username}
          onChange={(e) => setUsername(e.target.value)} autoComplete="username"
          placeholder="how others will see you" required />


        <label style={styles.label}>New password *</label>
        <input style={styles.input} type="password" value={next}
          onChange={(e) => setNext(e.target.value)} autoComplete="new-password" required />
        <div style={styles.hint}>At least 8 characters, including a letter and a number.</div>

        <label style={styles.label}>Confirm new password *</label>
        <input style={styles.input} type="password" value={confirm}
          onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required />

        {error && <div style={styles.error}>{error}</div>}

        <button className="btn btn-primary" style={{ width: '100%', marginTop: 12 }} disabled={busy}>
          {busy ? 'Saving…' : 'Save & continue'}
        </button>
        <button type="button" style={styles.link} onClick={() => signOut()}>Sign out</button>
      </form>
    </div>
  );
}

function friendly(err) {
  const code = err?.code || '';
  switch (code) {
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'The temporary password is incorrect.';
    case 'auth/weak-password':
      return 'Please choose a stronger password (at least 8 characters).';
    case 'auth/requires-recent-login':
      return 'Please sign out and sign in again, then retry.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    default:
      return err?.message || 'Could not change the password. Please try again.';
  }
}

const styles = {
  wrap: { minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  card: {
    width: 400, background: '#fff', border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', padding: 28, boxShadow: 'var(--shadow-card)',
  },
  title: { fontSize: '1.25rem', marginBottom: 6, color: 'var(--ink)' },
  sub: { fontSize: '0.875rem', color: 'var(--muted)', marginBottom: 8 },
  label: { display: 'block', fontSize: '0.75rem', fontWeight: 600, color: 'var(--ink-3)', margin: '10px 0 4px' },
  input: {
    width: '100%', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    padding: '9px 12px', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box',
  },
  hint: { fontSize: '0.72rem', color: 'var(--muted)', marginTop: 4 },
  error: {
    marginTop: 12, background: 'var(--red-bg)', color: 'var(--red-text)',
    padding: '8px 10px', borderRadius: 'var(--radius-sm)', fontSize: '0.78125rem',
  },
  link: {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer', marginTop: 14,
    color: 'var(--muted)', fontSize: '0.8125rem', display: 'block', textAlign: 'center', width: '100%',
  },
};
